import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ApartmentOutlined,
    AuditOutlined,
    DesktopOutlined,
    EditOutlined,
    GlobalOutlined,
    MobileOutlined,
    SaveOutlined,
    StopOutlined,
} from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import ImageCol from '@components/common/table/ImageCol';
import { AppConstants, DATE_FORMAT_DISPLAY, DEFAULT_TABLE_ITEM_SIZE_LARGE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { statusOptions } from '@constants/masterData';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import RestaurantForm from '@modules/restaurant/RestaurantForm';
import useRestaurantFormModal from '@modules/restaurant/useRestaurantFormModal';
import routes from '@routes';
import { convertUtcToLocalTime, getDefaultSettings } from '@utils';
import { Button, Flex } from 'antd';

const message = defineMessages({
    objectName: 'restaurants',
});

const CREATE_FORM_ID = 'form-digibes-restaurant-create';

const RestaurantByCustomerListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const fullName = location.state?.fullName;
    const { customerId } = useParams();
    const statusValues = translate.formatKeys(statusOptions, ['label']);

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        openModal,
        onCancelModal,
        loadingSave,
        isEditing,
        isChanged,
        setIsChangedFormValues,
        onSave,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.restaurant.getList,
            getById: apiConfig.restaurant.getById,
            create: apiConfig.restaurant.create,
            update: apiConfig.restaurant.update,
            delete: apiConfig.restaurant.delete,
            changeStatus: apiConfig.restaurant.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };

            funcs.prepareGetListParams = () => {
                return {
                    accountId: customerId,
                };
            };

            funcs.additionalActionColumnButtons = () => ({
                edit: (record) => (
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            openRestaurantModal(record);
                        }}
                    >
                        <EditOutlined />
                    </Button>
                ),
                webPortal: ({ restaurantData }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.registration)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${AppConstants.webPortal}${restaurantData?.id}`, '_blank');
                            }}
                        >
                            <GlobalOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                devices: ({ id, restaurantName }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.devices)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                navigate(
                                    `${generatePath(routes.deviceTabsRestaurantListPage.path, {
                                        customerId: params.customerId,
                                    })}?tab=devices&restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <MobileOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                updateDatabase: (record) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.updateDatabase)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                openDatabaseModal(record);
                            }}
                        >
                            <ApartmentOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                thirdParty: ({ id, restaurantName }) => (
                    <BaseTooltip title={'Third party'}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                navigate(
                                    `${generatePath(routes.thirdPartyList.path, {
                                        customerId: params.customerId,
                                    })}?restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <AuditOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                tabletRequest: ({ id, restaurantName }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.tabletRequest)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                    `${generatePath(routes.tabletRequestListPage.path, {
                                        customerId: params.customerId,
                                    })}?restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <DesktopOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });

            funcs.getList = () => {
                const params = {
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    accountId: customerId,
                    ...queryFilter,
                };
                return mixinFuncs.handleFetchList(params);
            };
        },
    });

    const { openRestaurantModal, openDatabaseModal, renderModal } = useRestaurantFormModal({
        onReloadList: () => mixinFuncs.getList(),
    });

    const handleCreateForm = (values) => {
        const preparedData = {
            ...values,
            accountId: customerId,
            settings: getDefaultSettings(),
        };
        onSave(preparedData);
    };

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Logo" />,
            dataIndex: 'logoPath',
            align: 'center',
            width: 100,
            render: (logoPath) => <ImageCol path={logoPath ? `${AppConstants.contentRootUrl}${logoPath}` : null} />,
        },
        {
            title: <FormattedMessage defaultMessage="Restaurant Name" />,
            dataIndex: 'restaurantName',
            // width: 180, 
            render: (text) => {
                return (
                    <div
                        style={{ cursor: 'pointer', color: '#4096ff' }}
                    >
                        {text}
                    </div>
                );
            },
        },
        {
            title: <FormattedMessage defaultMessage="Address" />,
            dataIndex: ['address'],
        },
        {
            title: <FormattedMessage defaultMessage="ZIP code" />,
            width: 90,
            dataIndex: ['zipCode'],
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expiredDate',
            align: 'right',
            render: (expiredDate) => {
                const expiredDateLocal = convertUtcToLocalTime(expiredDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
                return <div>{expiredDateLocal}</div>;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Extend Date" />,
            width: 140,
            dataIndex: 'extendDate',
            render: (extendDate) => {
                const extendDateLocal = convertUtcToLocalTime(extendDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
                return <div>{extendDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                // updateDatabase: true,
                // devices: true,
                // thirdParty: true,
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'restaurantName',
            placeholder: translate.formatMessage(commonMessage.restaurantName),
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
        },
    ];

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.digibesShop),
                    path: routes.shopAccountListPage.path,
                },
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                },
            ]}
        >
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 'normal', fontSize: '16px' }}>{fullName}</span>
                        {mixinFuncs.renderActionBar()}
                    </div>
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={pagination}
                        onRow={(record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate(
                                    `${generatePath(routes.deviceTabsRestaurantListPage.path, {
                                        customerId: params.customerId,
                                    })}?tab=devices&restaurantId=${record?.id}&restaurantName=${record?.restaurantName}`,
                                );
                            },
                        })}
                    />
                }
            />
            {renderModal()}
            {openModal && !detail && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={false}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={CREATE_FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <RestaurantForm
                        formId={CREATE_FORM_ID}
                        dataDetail={{}}
                        isEditing={false}
                        actions={null}
                        onSubmit={handleCreateForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default RestaurantByCustomerListPage;
